import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Airplane,
  Clock,
  MapPin,
  MagnifyingGlass,
  Plus,
  Trash,
  Sparkle,
  AirplaneTakeoff,
  Buildings,
  UploadSimple,
  DownloadSimple,
  Export,
} from "@phosphor-icons/react";
import FlightMap from "@/components/FlightMap";
import InstallPWA from "@/components/InstallPWA";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [flights, setFlights] = useState([]);
  const [stats, setStats] = useState({
    total_flights: 0,
    total_hours: 0,
    unique_airports: 0,
    unique_airlines: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isAddFlightOpen, setIsAddFlightOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [mapData, setMapData] = useState({ airports: [], routes: [] });
  const [newFlight, setNewFlight] = useState({
    flight_number: "",
    airline: "",
    aircraft: "",
    departure_airport: "",
    arrival_airport: "",
    flight_date: new Date().toISOString().split("T")[0],
    flight_duration_minutes: "",
    notes: "",
  });

  const fetchFlights = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/flights`);
      setFlights(response.data);
    } catch (error) {
      console.error("Error fetching flights:", error);
      toast.error("Failed to fetch flights");
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const fetchAiSummary = useCallback(async () => {
    setIsLoadingSummary(true);
    try {
      const response = await axios.post(`${API}/ai/summary`, {});
      setAiSummary(response.data.summary);
    } catch (error) {
      console.error("Error fetching AI summary:", error);
      setAiSummary("Unable to generate summary at this time.");
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  const fetchMapData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/flights/map-data`);
      setMapData(response.data);
    } catch (error) {
      console.error("Error fetching map data:", error);
    }
  }, []);

  useEffect(() => {
    fetchFlights();
    fetchStats();
    fetchAiSummary();
    fetchMapData();
  }, [fetchFlights, fetchStats, fetchAiSummary, fetchMapData]);

  const searchFlights = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a flight number");
      return;
    }
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${API}/flights/search?flight_number=${encodeURIComponent(searchQuery)}`
      );
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info("No flights found");
      }
    } catch (error) {
      console.error("Error searching flights:", error);
      toast.error("Failed to search flights");
    } finally {
      setIsSearching(false);
    }
  };

  const logFlightFromSearch = async (searchResult) => {
    try {
      const flightData = {
        ...searchResult,
        flight_duration_minutes: 180, // Default estimate
        notes: "Auto-logged from flight search",
      };
      await axios.post(`${API}/flights`, flightData);
      toast.success("Flight logged successfully!");
      setSearchResults([]);
      setSearchQuery("");
      fetchFlights();
      fetchStats();
      fetchAiSummary();
      fetchMapData();
    } catch (error) {
      console.error("Error logging flight:", error);
      toast.error("Failed to log flight");
    }
  };

  const addManualFlight = async () => {
    if (
      !newFlight.flight_number ||
      !newFlight.departure_airport ||
      !newFlight.arrival_airport
    ) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      const flightData = {
        ...newFlight,
        flight_duration_minutes: newFlight.flight_duration_minutes
          ? parseInt(newFlight.flight_duration_minutes)
          : null,
      };
      await axios.post(`${API}/flights`, flightData);
      toast.success("Flight added successfully!");
      setIsAddFlightOpen(false);
      setNewFlight({
        flight_number: "",
        airline: "",
        aircraft: "",
        departure_airport: "",
        arrival_airport: "",
        flight_date: new Date().toISOString().split("T")[0],
        flight_duration_minutes: "",
        notes: "",
      });
      fetchFlights();
      fetchStats();
      fetchAiSummary();
      fetchMapData();
    } catch (error) {
      console.error("Error adding flight:", error);
      toast.error("Failed to add flight");
    }
  };

  const deleteFlight = async (flightId) => {
    try {
      await axios.delete(`${API}/flights/${flightId}`);
      toast.success("Flight deleted");
      fetchFlights();
      fetchStats();
      fetchAiSummary();
      fetchMapData();
    } catch (error) {
      console.error("Error deleting flight:", error);
      toast.error("Failed to delete flight");
    }
  };

  const importCSV = async () => {
    if (!importFile) {
      toast.error("Please select a CSV file");
      return;
    }
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const response = await axios.post(`${API}/flights/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, errors } = response.data;
      if (imported > 0) {
        toast.success(`Successfully imported ${imported} flights`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} rows had errors`);
        console.log("Import errors:", errors);
      }
      setIsImportOpen(false);
      setImportFile(null);
      fetchFlights();
      fetchStats();
      fetchAiSummary();
      fetchMapData();
    } catch (error) {
      console.error("Error importing CSV:", error);
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = "flight_number,departure_airport,arrival_airport,flight_date,flight_duration_minutes,airline,aircraft,notes\nAA123,JFK,LAX,2026-01-15,330,American Airlines,Boeing 737-800,Business trip\nUA456,SFO,ORD,2026-01-20,240,United Airlines,Airbus A320,";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flight_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportFlights = async () => {
    try {
      const response = await axios.get(`${API}/flights/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flight_logbook_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Flights exported successfully!");
    } catch (error) {
      console.error("Error exporting flights:", error);
      toast.error("Failed to export flights");
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      landed: "status-badge-landed",
      scheduled: "status-badge-scheduled",
      active: "status-badge-active",
      "en-route": "status-badge-active",
    };
    return statusMap[status?.toLowerCase()] || "status-badge-scheduled";
  };

  return (
    <div className="app-container min-h-screen">
      <Toaster position="top-right" richColors />
      <InstallPWA />

      {/* Header */}
      <header className="app-header sticky top-0 z-50 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Airplane
              weight="duotone"
              className="w-8 h-8 text-[#007AFF]"
              data-testid="app-logo"
            />
            <h1
              className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight"
              data-testid="app-title"
            >
              Flight Logbook
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportFlights}
              disabled={flights.length === 0}
              className="btn-tactical border-white/10 text-white hover:bg-white/5 rounded-sm"
              data-testid="export-csv-button"
            >
              <Export weight="bold" className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="btn-tactical border-white/10 text-white hover:bg-white/5 rounded-sm"
                  data-testid="import-csv-button"
                >
                  <UploadSimple weight="bold" className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#111111] border-white/10 text-white" aria-describedby="import-csv-description">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase tracking-tight">
                    Import Flights from CSV
                  </DialogTitle>
                  <p id="import-csv-description" className="text-sm text-white/60">
                    Upload a CSV file with your flight history
                  </p>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-sm">
                    <p className="data-label mb-2">Required Columns</p>
                    <p className="text-sm text-white/70 font-mono">
                      flight_number, departure_airport, arrival_airport, flight_date
                    </p>
                    <p className="data-label mt-3 mb-2">Optional Columns</p>
                    <p className="text-sm text-white/70 font-mono">
                      flight_duration_minutes, airline, aircraft, notes
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    className="border-white/10 text-white hover:bg-white/5 rounded-sm"
                    data-testid="download-template-button"
                  >
                    <DownloadSimple weight="bold" className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <div>
                    <Label className="data-label">Select CSV File</Label>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white file:bg-[#007AFF] file:text-white file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded-sm"
                      data-testid="csv-file-input"
                    />
                  </div>
                  {importFile && (
                    <p className="text-sm text-white/60">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>
                <Button
                  onClick={importCSV}
                  disabled={!importFile || isImporting}
                  className="btn-tactical w-full bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-sm"
                  data-testid="import-button"
                >
                  {isImporting ? "Importing..." : "Import Flights"}
                </Button>
              </DialogContent>
            </Dialog>
            <Dialog open={isAddFlightOpen} onOpenChange={setIsAddFlightOpen}>
            <DialogTrigger asChild>
              <Button
                className="btn-tactical bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-sm"
                data-testid="add-flight-button"
              >
                <Plus weight="bold" className="w-4 h-4 mr-2" />
                Add Flight
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111111] border-white/10 text-white" aria-describedby="add-flight-description">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase tracking-tight">
                  Add Manual Flight
                </DialogTitle>
                <p id="add-flight-description" className="text-sm text-white/60">
                  Enter flight details to add to your logbook
                </p>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="data-label">Flight Number *</Label>
                    <Input
                      data-testid="manual-flight-number"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.flight_number}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          flight_number: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="AA123"
                    />
                  </div>
                  <div>
                    <Label className="data-label">Airline</Label>
                    <Input
                      data-testid="manual-airline"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.airline}
                      onChange={(e) =>
                        setNewFlight({ ...newFlight, airline: e.target.value })
                      }
                      placeholder="American Airlines"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="data-label">From (Airport) *</Label>
                    <Input
                      data-testid="manual-departure"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.departure_airport}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          departure_airport: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="JFK"
                    />
                  </div>
                  <div>
                    <Label className="data-label">To (Airport) *</Label>
                    <Input
                      data-testid="manual-arrival"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.arrival_airport}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          arrival_airport: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="LAX"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="data-label">Date</Label>
                    <Input
                      data-testid="manual-date"
                      type="date"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.flight_date}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          flight_date: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="data-label">Duration (minutes)</Label>
                    <Input
                      data-testid="manual-duration"
                      type="number"
                      className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                      value={newFlight.flight_duration_minutes}
                      onChange={(e) =>
                        setNewFlight({
                          ...newFlight,
                          flight_duration_minutes: e.target.value,
                        })
                      }
                      placeholder="180"
                    />
                  </div>
                </div>
                <div>
                  <Label className="data-label">Aircraft</Label>
                  <Input
                    data-testid="manual-aircraft"
                    className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                    value={newFlight.aircraft}
                    onChange={(e) =>
                      setNewFlight({ ...newFlight, aircraft: e.target.value })
                    }
                    placeholder="Boeing 737-800"
                  />
                </div>
                <div>
                  <Label className="data-label">Notes</Label>
                  <Input
                    data-testid="manual-notes"
                    className="tactical-input mt-1 bg-[#0A0A0A] border-white/10 text-white"
                    value={newFlight.notes}
                    onChange={(e) =>
                      setNewFlight({ ...newFlight, notes: e.target.value })
                    }
                    placeholder="First class upgrade!"
                  />
                </div>
              </div>
              <Button
                onClick={addManualFlight}
                className="btn-tactical w-full bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-sm"
                data-testid="save-flight-button"
              >
                Save Flight
              </Button>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
        {/* Stats Row */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
          data-testid="stats-section"
        >
          <Card className="stats-card bg-[#111111] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-[#007AFF]/20">
                  <Airplane weight="duotone" className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="data-label">Total Flights</p>
                  <p
                    className="data-value text-white font-mono text-2xl"
                    data-testid="total-flights"
                  >
                    {stats.total_flights}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stats-card bg-[#111111] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-[#34C759]/20">
                  <Clock weight="duotone" className="w-5 h-5 text-[#34C759]" />
                </div>
                <div>
                  <p className="data-label">Flight Hours</p>
                  <p
                    className="data-value text-white font-mono text-2xl"
                    data-testid="total-hours"
                  >
                    {stats.total_hours}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stats-card bg-[#111111] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-[#FF3B30]/20">
                  <MapPin weight="duotone" className="w-5 h-5 text-[#FF3B30]" />
                </div>
                <div>
                  <p className="data-label">Airports</p>
                  <p
                    className="data-value text-white font-mono text-2xl"
                    data-testid="total-airports"
                  >
                    {stats.unique_airports}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stats-card bg-[#111111] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-[#FFD60A]/20">
                  <Buildings
                    weight="duotone"
                    className="w-5 h-5 text-[#FFD60A]"
                  />
                </div>
                <div>
                  <p className="data-label">Airlines</p>
                  <p
                    className="data-value text-white font-mono text-2xl"
                    data-testid="total-airlines"
                  >
                    {stats.unique_airlines}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and AI Summary Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Flight Search */}
          <Card className="tactical-card bg-[#111111] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                <MagnifyingGlass weight="duotone" className="w-5 h-5 text-[#007AFF]" />
                Search & Log Flight
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="flight-search-input"
                  className="tactical-input bg-[#0A0A0A] border-white/10 text-white flex-1"
                  placeholder="Enter flight number (e.g., AA123)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && searchFlights()}
                />
                <Button
                  onClick={searchFlights}
                  disabled={isSearching}
                  className="btn-tactical bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-sm"
                  data-testid="search-button"
                >
                  {isSearching ? "..." : "Search"}
                </Button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-2" data-testid="search-results">
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      className="p-3 bg-[#0A0A0A] border border-white/10 rounded-sm flex items-center justify-between"
                    >
                      <div>
                        <p className="font-mono text-white">
                          {result.flight_number}
                        </p>
                        <p className="text-sm text-white/60">
                          {result.departure_airport} → {result.arrival_airport} |{" "}
                          {result.flight_date}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => logFlightFromSearch(result)}
                        className="btn-tactical bg-[#34C759] hover:bg-[#2ea34a] text-white rounded-sm"
                        data-testid={`log-flight-${index}`}
                      >
                        <AirplaneTakeoff weight="bold" className="w-4 h-4 mr-1" />
                        Log
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Summary */}
          <Card
            className="ai-summary-card bg-gradient-to-br from-[#111111] to-[#0A0A0A] border-white/10"
            data-testid="ai-summary-card"
          >
            <CardHeader className="pb-2 border-t-2 border-t-[#007AFF]">
              <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                <Sparkle weight="duotone" className="w-5 h-5 text-[#007AFF]" />
                AI Flight Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-2">
                  <div className="h-4 skeleton rounded w-full"></div>
                  <div className="h-4 skeleton rounded w-3/4"></div>
                  <div className="h-4 skeleton rounded w-5/6"></div>
                </div>
              ) : (
                <p
                  className="text-white/80 leading-relaxed"
                  data-testid="ai-summary-text"
                >
                  {aiSummary}
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAiSummary}
                disabled={isLoadingSummary}
                className="mt-4 border-white/10 text-white hover:bg-white/5 rounded-sm"
                data-testid="refresh-summary-button"
              >
                <Sparkle weight="bold" className="w-4 h-4 mr-2" />
                Refresh Insights
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Interactive Map */}
        <Card className="tactical-card bg-[#111111] border-white/10 mb-6 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
              <MapPin weight="duotone" className="w-5 h-5 text-[#007AFF]" />
              Flight Routes Map
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div
              className="map-container relative bg-[#0A0A0A]"
              data-testid="map-container"
            >
              <FlightMap mapData={mapData} />
            </div>
          </CardContent>
        </Card>

        {/* Flight Logbook Table */}
        <Card
          className="tactical-card bg-[#111111] border-white/10"
          data-testid="flight-logbook"
        >
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
              <Airplane weight="duotone" className="w-5 h-5 text-[#007AFF]" />
              Flight Logbook
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {flights.length === 0 ? (
              <div className="p-8 text-center text-white/60">
                <Airplane
                  weight="duotone"
                  className="w-12 h-12 mx-auto mb-4 opacity-30"
                />
                <p>No flights logged yet</p>
                <p className="text-sm">
                  Search for a flight or add one manually to get started
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="data-label text-white/60">
                        Date
                      </TableHead>
                      <TableHead className="data-label text-white/60">
                        Flight
                      </TableHead>
                      <TableHead className="data-label text-white/60">
                        Route
                      </TableHead>
                      <TableHead className="data-label text-white/60">
                        Duration
                      </TableHead>
                      <TableHead className="data-label text-white/60">
                        Status
                      </TableHead>
                      <TableHead className="data-label text-white/60 text-right">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flights.map((flight) => (
                      <TableRow
                        key={flight.id}
                        className="flight-row border-white/5"
                        data-testid={`flight-row-${flight.id}`}
                      >
                        <TableCell className="font-mono text-white/80">
                          {flight.flight_date}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-white font-medium">
                            {flight.flight_number}
                          </span>
                          {flight.airline && (
                            <span className="text-white/50 text-sm block">
                              {flight.airline}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">
                          <span className="text-[#007AFF]">
                            {flight.departure_airport}
                          </span>
                          <span className="text-white/40 mx-2">→</span>
                          <span className="text-[#34C759]">
                            {flight.arrival_airport}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-white/80">
                          {flight.flight_duration_minutes
                            ? `${Math.floor(flight.flight_duration_minutes / 60)}h ${flight.flight_duration_minutes % 60}m`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`status-badge rounded-sm ${getStatusBadge(flight.status)}`}
                          >
                            {flight.status || "logged"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteFlight(flight.id)}
                            className="text-white/40 hover:text-[#FF3B30] hover:bg-transparent"
                            data-testid={`delete-flight-${flight.id}`}
                          >
                            <Trash weight="bold" className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="px-4 sm:px-6 lg:px-8 py-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-white/40 text-sm">
          <p>Flight Logbook - Track Your Journey Across the Skies</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
